import React from "react";
import { useI18n } from "../lib/i18n";

/**
 * Vision 2030 alignment badge.
 *
 * Two variants:
 *   • compact  → small footer pill (icon + "Aligned with Vision 2030")
 *   • hero     → prominent dark glass card with the year mark + tagline,
 *                designed to sit beside the homepage CTA.
 *
 * Colors use the official Saudi green (#006C35) accented with CARTAL gold.
 */
export default function Vision2030Badge({ variant = "compact" }) {
  const { lang } = useI18n();
  if (variant === "hero") return <HeroVariant lang={lang} />;
  return <CompactVariant lang={lang} />;
}

function Vision2030Mark({ size = 28 }) {
  // A clean stylised "2030" emblem in Saudi green with a gold palm-and-arc
  // accent. Visually aligned with the national identity without reproducing
  // the official trademarked emblem.
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="v30-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0a7a3d" />
          <stop offset="0.6" stopColor="#006C35" />
          <stop offset="1" stopColor="#063e21" />
        </linearGradient>
        <linearGradient id="v30-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e8c873" />
          <stop offset="1" stopColor="#a8893e" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill="url(#v30-grad)" />
      <circle cx="32" cy="32" r="29" fill="none" stroke="url(#v30-gold)" strokeWidth="0.8" opacity="0.55" />
      {/* Palm-suggestion arcs */}
      <path d="M16 47 Q 32 32 48 47" stroke="url(#v30-gold)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.8" />
      <path d="M18 51 Q 32 41 46 51" stroke="url(#v30-gold)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55" />
      <text x="32" y="34" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fontSize="18" fill="#ffffff" letterSpacing="-0.6">2030</text>
      <text x="32" y="43" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontWeight="700" fontSize="5.5" fill="url(#v30-gold)" letterSpacing="2">VISION</text>
    </svg>
  );
}

function CompactVariant({ lang }) {
  return (
    <div
      className="inline-flex items-center gap-2.5 rounded-full px-3 py-1.5 transition-all hover:scale-[1.03]"
      style={{
        background: "rgba(0,108,53,0.06)",
        border: "1px solid rgba(0,108,53,0.3)",
      }}
      data-testid="vision-2030-badge-compact"
      title={lang === "ar" ? "متوافق مع رؤية المملكة 2030" : "Aligned with Saudi Vision 2030"}
    >
      <Vision2030Mark size={22} />
      <div className="text-[10px] leading-tight">
        <div className="font-bold text-[#006C35]">
          {lang === "ar" ? "نسير مع رؤية" : "Aligned with"}
        </div>
        <div className="font-bold text-[#006C35]">
          {lang === "ar" ? "المملكة 2030" : "Saudi Vision 2030"}
        </div>
      </div>
    </div>
  );
}

function HeroVariant({ lang }) {
  return (
    <div
      className="relative rounded-2xl px-5 py-4 overflow-hidden group transition-all hover:shadow-lg"
      style={{
        background: "linear-gradient(135deg, #006C35 0%, #0a4f3a 60%, #003d1f 100%)",
      }}
      data-testid="vision-2030-badge-hero"
    >
      {/* Decorative gold accents */}
      <div
        className="absolute -top-6 -end-6 w-24 h-24 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ background: "radial-gradient(circle, #c9a55c 0%, transparent 70%)" }}
      />
      <div className="absolute bottom-0 start-0 end-0 h-[3px]" style={{ background: "linear-gradient(90deg, transparent, #c9a55c, transparent)" }} />

      <div className="relative flex items-center gap-4">
        <Vision2030Mark size={56} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: "#c9a55c" }}>
            {lang === "ar" ? "شريك في التحوّل" : "Partner in transformation"}
          </div>
          <div className="font-display text-lg sm:text-xl text-white mt-0.5" style={{ lineHeight: 1.2 }}>
            {lang === "ar" ? "كارتال تواكب رؤية المملكة 2030" : "CARTAL walks alongside Saudi Vision 2030"}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
            {lang === "ar"
              ? "اقتصاد رقمي · ابتكار محلي · ثقة عالية في خدمات السيارات."
              : "Digital economy · Local innovation · High-trust automotive services."}
          </div>
        </div>
      </div>
    </div>
  );
}
